/**
 * Nursery Engine — High-Performance API Proxy Engine
 * 
 * Shifted away from massive client-side pools to a lightweight server-side pagination model.
 * The engine now tracks only the currently visible tokens and live-merges WebSocket updates.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

type TradeEntry = { ts: number; isBuy: boolean; sol: number; user?: string; mcSol?: number };

export type PreBondToken = {
  mint: string;
  name: string;
  symbol: string;
  firstSeenMs: number;
  lastTradeMs: number;
  marketCapSol: number;
  buys5m: number;
  sells5m: number;
  vol5mSol: number;
  maxSingleBuySol: number;
  imageUri: string | null;
  isDead?: boolean;
  isRugged?: boolean;
  ruggedAtMs?: number; 
  peakMcSol: number; 
  largeTradeCluster: number; 
  recentLargeTrades: TradeEntry[]; 
  tape: TradeEntry[]; 
  momentumBurst: boolean; 
  runnerScore?: number; 
  whaleCount: number;
  whaleWallets: string[]; 
  consecutiveBuys: number; 
  traderCount: number; 
  phantomTrade?: {
    entryMcSol: number;
    exitMcSol?: number;
    pnlPct?: number;
    status: "open" | "won" | "lost";
  };
  creator?: string;

  checked?: boolean;
  hidden?: boolean;
  hideReason?: string | null;
  creatorPct?: number | null;
  holderTop10?: number | null;
  holderTop1?: number | null;
  bundled?: boolean;
  rugFake?: boolean;
  earlyDumped?: boolean;
  dipPct?: number;
  maxDipPct?: number;
  revived?: boolean;
  stale?: boolean;
  marketCapUsd?: number;
};

export type BondedToken = {
  mint: string;
  name: string;
  symbol: string;
  bondedMs: number;
  priceUsd: number;
  marketCapUsd: number;
  liquidityUsd: number;
  vol1h: number;
  vol24h: number;
  buys1h: number;
  sells1h: number;
  priceChange1h: number;
  priceChange24h: number;
  lastUpdatedMs: number;
  revivalScore: number;
  imageUri: string | null;
  firstSeenMs: number;
  lastSeenMs: number;
  maxBuySol: number;
  totalVolSol: number;
  largeTradeCount: number; 
  mediumTradeCount: number; 
};

export type NurseryCounts = {
  new: { tradable: number, checked: number, blocked: number, unchecked: number, total: number };
  grad: { tradable: number, checked: number, blocked: number, unchecked: number, total: number };
  runners: { total: number };
  bonded: { tradable: number, blocked: number, unchecked: number, stale: number, total: number };
  total: number;
  hydrated: boolean;
};

export type NurserySnapshot = {
  visibleTokens: any[];
  visibleTotal: number;
  counts: NurseryCounts | null;
  isServerSideActive: boolean;
  recentDeployments: any[];
};

// ── State ─────────────────────────────────────────────────────────────────────

let visibleTokens: any[] = [];
let recentDeployments: any[] = [];
let visibleTotal: number = 0;
let currentCounts: NurseryCounts | null = null;
let serverSideActive = false;
let discoveryWs: WebSocket | null = null;
let running = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

let currentLaunchProviderFilter: string[] = ["Pump.fun", "Moonshot", "Raydium", "Meteora"];

export function setLaunchProviderFilter(filter: string[]) {
  currentLaunchProviderFilter = filter;
}

export function isServerSideActive() {
  return serverSideActive;
}

export function getSnapshot(): NurserySnapshot {
  const isPumpAllowed = currentLaunchProviderFilter.includes("Pump.fun");
  const filterCoin = (t: any) => {
    if (isPumpAllowed) return true;
    return !JSON.stringify(t).toLowerCase().includes("pump");
  };

  return {
    visibleTokens: visibleTokens.filter(filterCoin),
    visibleTotal,
    counts: currentCounts,
    isServerSideActive: serverSideActive,
    recentDeployments: recentDeployments.filter(filterCoin),
  };
}

export function getNurseryToken(mint: string): PreBondToken | null {
  if (!mint) return null;
  return visibleTokens.find((t) => t.mint === mint) || recentDeployments.find((t) => t.mint === mint) || null;
}

export function injectNewToken(token: any) {
  // Only inject if it's not already in the array
  if (!visibleTokens.find(t => t.mint === token.mint)) {
    visibleTokens.unshift(token);
    visibleTotal += 1;
    // We explicitly avoid manually incrementing currentCounts here.
    // The UI now streams raw DB counts in the background.
  }
}

export async function fetchPage(tab: string, subtab: string, sortKey: string, sortDir: string, skip: number, limit: number, append: boolean = false, devWallet?: string, q?: string) {
  try {
    const url = new URL(window.location.origin + "/api/nursery-tokens");
    url.searchParams.set("tab", tab);
    if (subtab) url.searchParams.set("subtab", subtab);
    if (sortKey) url.searchParams.set("sort", sortKey);
    url.searchParams.set("sortDir", sortDir);
    url.searchParams.set("skip", skip.toString());
    url.searchParams.set("limit", limit.toString());
    if (devWallet) url.searchParams.set("devWallet", devWallet);
    if (q) url.searchParams.set("q", q);

    const res = await fetch(url.toString(), {
      headers: { "x-lily-app": "1" }
    });
    if (res.ok) {
      const data = await res.json();
      if (append) {
        visibleTokens = [...visibleTokens, ...(data.coins || [])];
      } else {
        visibleTokens = data.coins || [];
      }
      visibleTotal = data.total || 0;
    }
  } catch (err) {
    console.error("[NURSERY ENGINE] Failed to fetch page", err);
  }
}

export async function refreshCounts() {
  try {
    const res = await fetch("/api/nursery-counts", {
      headers: { "x-lily-app": "1" }
    });
    if (res.ok) {
      currentCounts = await res.json();
    }
  } catch (err) {}
}

function processDiscoveryWsMessage(data: any) {
  try {
    const { new: newFeed, bonded: bondedFeed, old: oldFeed } = data;
    const allUpdates = [
      ...(newFeed?.coins || []),
      ...(bondedFeed?.coins || []),
      ...(oldFeed?.coins || [])
    ];

    if (allUpdates.length === 0) return;

    const updateMap = new Map();
    for (const u of allUpdates) updateMap.set(u.mint, u);

    // Only update tokens that are CURRENTLY visible in the UI
    for (let i = 0; i < visibleTokens.length; i++) {
      const t = visibleTokens[i];
      if (updateMap.has(t.mint)) {
        visibleTokens[i] = { ...t, ...updateMap.get(t.mint) };
      }
    }

    // Keep track of recent deployments globally for toast notifications
    if (newFeed?.coins?.length > 0) {
      const fresh = newFeed.coins.filter((c: any) => Date.now() - (c.firstSeenMs || 0) < 30000);
      if (fresh.length > 0) {
        // Prepend fresh coins and keep the most recent 100
        recentDeployments = [...fresh, ...recentDeployments].slice(0, 100);
      }
    }
  } catch (err) {
    console.error("[NURSERY ENGINE] Error processing WS message", err);
  }
}

async function checkServerHealth(): Promise<boolean> {
  try {
    const res = await fetch("/api/health");
    if (!res.ok) return false;
    const data = await res.json();
    return !!data.discovery;
  } catch {
    return false;
  }
}

function connectServerDiscoveryWs() {
  if (discoveryWs) {
    discoveryWs.close();
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${protocol}//${window.location.host}/api/discovery-ws`;
  
  console.log(`[NURSERY ENGINE] Connecting to Discovery WS at ${wsUrl}`);
  discoveryWs = new WebSocket(wsUrl);

  discoveryWs.onopen = () => {
    console.log("[NURSERY ENGINE] Discovery WS connected.");
    serverSideActive = true;
  };

  discoveryWs.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'discovery_snapshot') {
        processDiscoveryWsMessage(data);
      }
    } catch (e) {
      console.error("[NURSERY ENGINE] Failed to parse WS message", e);
    }
  };

  discoveryWs.onclose = () => {
    console.warn("[NURSERY ENGINE] Discovery WS closed. Reconnecting in 3s...");
    serverSideActive = false;
    if (running) {
      reconnectTimer = setTimeout(connectServerDiscoveryWs, 3000);
    }
  };

  discoveryWs.onerror = (err) => {
    console.error("[NURSERY ENGINE] Discovery WS error:", err);
  };
}

export async function start() {
  if (running) return;
  running = true;
  await refreshCounts();
  
  checkServerHealth().then((isAvailable) => {
    if (isAvailable) {
      serverSideActive = true;
      connectServerDiscoveryWs();
    } else {
      serverSideActive = false;
    }
  });
  
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = setInterval(refreshCounts, 10000); // refresh counts every 10s
}

export function stop() {
  running = false;
  serverSideActive = false;
  if (discoveryWs) {
    discoveryWs.close();
    discoveryWs = null;
  }
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

export async function rehydrate() {
  visibleTokens = [];
  visibleTotal = 0;
  await refreshCounts();
}

export async function loadMorePrebond() {
  // Legacy stub just in case
}

